import type { Undefinable } from "@core/domain/types/undefinable.type";
import { normalizeText } from "@core/domain/utils/normalize-text";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { Farm, Farms } from "@farm/domain/models/farm.model";
import { FarmDeleteUseCase } from "@farm/domain/usecases/farm-delete.usecase";
import { FarmGetAllUseCase } from "@farm/domain/usecases/farm-get-all.usecase";
import { FarmGetByIdUseCase } from "@farm/domain/usecases/farm-get-by-id.usecase";
import { BaseViewModel } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

export interface FarmListSearchFilters {
    search: string;
}

@injectable()
export class FarmListViewmodel extends BaseViewModel {
    @observable
    farms: Farms = Farms.empty();

    initialFiltersValue: FarmListSearchFilters = {
        search: "",
    };

    @observable filters: FarmListSearchFilters = this.initialFiltersValue;

    @observable
    searchResults: { label: string; id: number }[] = [];

    @observable
    showCreateFarmModal = false;

    @observable
    showEditFarmModal = false;

    @observable
    farm: Undefinable<Readonly<Farm>> = undefined;

    constructor(
        @inject(FarmGetAllUseCase)
        private readonly farmGetAllUseCase: FarmGetAllUseCase,
        @inject(FarmDeleteUseCase)
        private readonly farmDeleteUseCase: FarmDeleteUseCase,
        @inject(FarmGetByIdUseCase)
        private readonly farmGetByIdUseCase: FarmGetByIdUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.fetchFarms();
    }

    @action
    private async fetchFarms(): Promise<void> {
        const farms = await this.farmGetAllUseCase.execute();
        runInAction(() => {
            this.farms = farms;
            this.updateSearchResults();
        });
    }

    async reloadFarms(): Promise<void> {
        LoadLayoutStore.start();
        await this.fetchFarms();
        LoadLayoutStore.finish();
    }

    @action
    toggleShowCreateFarmModal(): void {
        this.showCreateFarmModal = !this.showCreateFarmModal;
    }

    @action
    toggleShowEditFarmModal(): void {
        this.showEditFarmModal = !this.showEditFarmModal;
    }

    @action
    setFilters(filters: { search: string }): void {
        this.filters = filters;
        this.updateSearchResults();
    }

    @action
    updateSearchResults(): void {
        const query = normalizeText(this.filters.search);

        if (!query) {
            this.searchResults = this.farms.farms.map((farm) => ({
                label: farm.name,
                id: Number(farm.id.value),
            }));
            return;
        }

        this.searchResults = this.farms.farms
            .filter((farm) => normalizeText(farm.name).includes(query))
            .map((farm) => ({
                label: farm.name,
                id: Number(farm.id.value),
            }));
    }

    @action
    async deleteFarm(farmId: number): Promise<void> {
        const deleted = await this.farmDeleteUseCase.execute(farmId);

        if (deleted) {
            await this.fetchFarms();
        }
    }

    @action
    async fetchFarmById(farmId: number): Promise<void> {
        const farm = await this.farmGetByIdUseCase.execute(farmId);
        runInAction(() => {
            this.farm = farm ?? undefined;
        });
    }
}
